import { z } from 'zod';
import type {
  CreateServiceRequest,
  Service,
  UpdateServiceRequest,
} from '@repo/api-client/dtos';
import { minorToDecimal } from '@/features/invoice/invoiceFormSchema';

/**
 * Service form. Pricing rate lives as a user-decimal string in the
 * form and is lifted via `parseUserMoney` on submit before being
 * serialised to the wire's minor-units shape.
 */
export const serviceFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().trim().optional().or(z.literal('')),
  pricingRateInput: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine(
      (v) => /^-?\d+(?:[\.,]\d+)?$/.test(v),
      'Enter a number (e.g. 100.00)',
    ),
  pricingCurrency: z.string().min(1).max(3),
  pricingUnit: z.string().min(1).default('fixed'),
  pricingCustomUnit: z.string().trim().optional().or(z.literal('')),
  taxable: z.boolean().default(true),
  taxRate: z
    .string()
    .trim()
    .optional()
    .or(z.literal(''))
    .refine(
      (v) => !v || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100),
      '0–100',
    ),
  isActive: z.boolean().default(true),
});

export type ServiceFormValues = z.infer<typeof serviceFormSchema>;

export function serviceFormDefaults(currency: string): ServiceFormValues {
  return {
    name: '',
    description: '',
    pricingRateInput: '',
    pricingCurrency: currency,
    pricingUnit: 'fixed',
    pricingCustomUnit: '',
    taxable: true,
    taxRate: '',
    isActive: true,
  };
}

export function serviceFormFromWire(service: Service): ServiceFormValues {
  return {
    name: service.name,
    description: service.description ?? '',
    pricingRateInput: minorToDecimal(service.pricingRate, service.pricingCurrency),
    pricingCurrency: service.pricingCurrency,
    pricingUnit: service.pricingUnit,
    pricingCustomUnit: service.pricingCustomUnit ?? '',
    taxable: service.taxable,
    taxRate: typeof service.taxRate === 'number' ? String(service.taxRate) : '',
    isActive: service.isActive,
  };
}

export function serviceFormToCreate(
  values: ServiceFormValues,
  pricingRateMinor: string,
): CreateServiceRequest {
  return {
    name: values.name,
    description: values.description || undefined,
    pricingRate: pricingRateMinor,
    pricingCurrency: values.pricingCurrency,
    pricingUnit: values.pricingUnit,
    pricingCustomUnit: values.pricingCustomUnit || undefined,
    taxable: values.taxable,
    ...(values.taxRate ? { taxRate: Number(values.taxRate) } : {}),
    tags: [],
    isActive: values.isActive,
  };
}

export function serviceFormToUpdate(
  values: ServiceFormValues,
  pricingRateMinor: string,
): UpdateServiceRequest {
  return {
    name: values.name,
    description: values.description || undefined,
    pricingRate: pricingRateMinor,
    pricingCurrency: values.pricingCurrency,
    pricingUnit: values.pricingUnit,
    pricingCustomUnit: values.pricingCustomUnit || undefined,
    taxable: values.taxable,
    ...(values.taxRate ? { taxRate: Number(values.taxRate) } : { taxRate: undefined }),
    isActive: values.isActive,
  };
}
